import { getSortParams } from '@/utils/utils';
import { request } from '@umijs/max';
import { SortOrder } from 'antd/es/table/interface';

const sponsorService = {
  getSponsors: async (options?: {
    [key: string]: any;
  }): Promise<API.Service.Sponsor[]> => {
    const response = await request<API.Service.Sponsor[]>('/admin/sponsor', {
      method: 'GET',
      ...(options || {}),
    });

    // 自定义排序逻辑：将名称中包含 "Shuffle" 的项目放在最前面
    const sortedResponse = [...response].sort((a, b) => {
      const aHasShuffle = a.name.includes('Shuffle');
      const bHasShuffle = b.name.includes('Shuffle');

      if (aHasShuffle && !bHasShuffle) return -1;
      if (!aHasShuffle && bHasShuffle) return 1;
      return 0;
    });

    return sortedResponse as API.Service.Sponsor[];
  },

  getStateLogs: async (params: any) => {
    const res = await request(`/admin/sponsors/${params.sponsorId}/state-logs`, { method: 'GET', params });
    return { data: res.data, total: res.total, success: true };
  },
  
  selectStateLog: async (sponsorId: string, logId: string) => {
    return request(`/admin/sponsors/${sponsorId}/state-logs/${logId}/select`, { method: 'POST' });
  },

  clearStateLog: async (sponsorId: string) => {
    return request(`/admin/sponsors/${sponsorId}/state-logs/clear`, { method: 'POST' });
  },

  getStateLogDetails: async (sponsorId: string, logId: string) => {
    const res = await request(`/admin/sponsors/${sponsorId}/state-logs/${logId}/details`, { 
      method: 'GET' 
    });
    return { data: res.data, total: res.total, success: true };
  },

  async createSponsor({
    body,
    options,
  }: {
    body: API.Service.SponsorForm;
    options?: { [key: string]: any };
  }) {
    const response = await request('/admin/sponsor', {
      method: 'POST',
      data: body,
      ...(options || {}),
    });

    return response;
  },

  async updateSponsor({
    body,
    options,
  }: {
    body: API.Service.SponsorForm;
    options?: { [key: string]: any };
  }) {
    const response = await request(`/admin/sponsor`, {
      method: 'PUT',
      data: body,
      ...(options || {}),
    });

    return response;
  },

  async updateCurrentLB({
    body,
    options
  }: {
    body: API.Service.CurrentLeaderBoard;
    options?: { [key: string]: any };
  }) {
    const response = await request(`/admin/sponsor/currentLB`, {
      method: 'POST',
      data: body,
      ...(options || {}),
    });

    return response;
  },

  async deleteSponsor({ id }: { id: string }) {
    const response = await request(`/admin/sponsor/${id}`, {
      method: 'DELETE',
    });

    return response;
  },

  async syncSponsorData({ id }: { id: string }) {
    const response = await request(`/admin/sponsor/${id}/sync`, {
      method: 'POST',
    });

    return response;
  },

  async getSponsorsUsers(
    params: {
      id: string;
      // page: number;
      // size: number;

      name?: string;
      pageSize?: number;
      current?: number;
      // sortColumn?: string | null;
      // sortOrder?: string;
      // sort?: string;
    },
    sorter?: Record<string, SortOrder>,
    options?: { [key: string]: any },
  ): Promise<any> {
    const url = `/admin/sponsor/${params.id}/users`;

    const sort = getSortParams(sorter);

    const current = params.current || 1;
    const pageSize = params.pageSize || 20;

    const response = await request<API.Service.SponsorUser[]>(url, {
      method: 'GET',
      params: {
        page: current - 1,
        size: pageSize,
        username: params.name,
        ...(sort ? { sort } : {}),
      },
      getResponse: true,
      ...(options || {}),
    });

    return {
      data: response.data?.map((item, index) => ({
        ...item,
        serialNumber: (current - 1) * pageSize + index + 1,
      })),
      total: parseInt(response.headers['x-total-count'], 10),
      success: true,
    };
  },

  async getSponsorsUsers2(
    params: {
      id: string;
      name?: string;
      pageSize?: number;
      current?: number;
    },
    sorter?: Record<string, SortOrder>,
    options?: { [key: string]: any },
  ): Promise<any> {
    const url = `/admin/sponsor/${params.id}/users2`;

    const sort = getSortParams(sorter);

    const current = params.current || 1;
    const pageSize = params.pageSize || 20;

    const response = await request<API.Service.SponsorUser2[]>(url, {
      method: 'GET',
      params: {
        page: current - 1,
        size: pageSize,
        username: params.name,
        ...(sort ? { sort } : {}),
      },
      getResponse: true,
      ...(options || {}),
    });

    return {
      data: response.data?.map((item, index) => ({
        ...item,
        serialNumber: (current - 1) * pageSize + index + 1,
      })),
      total: parseInt(response.headers['x-total-count'], 10),
      success: true,
    };
  },

  async abuseAmountUser({
    id,
    body,
  }: {
    id: string;
    body: {
      modifyWagered: number;
      userId: string;
      abuseCoefficient: number;
      modifyDisplayedWagered: number;
    };
  }) {
    const response = await request(
      `/admin/sponsor/${id}/leaderboard/abuse-coefficient`,
      {
        method: 'PUT',
        data: body,
      },
    );

    return response;
  },

  async clearAbuseAmounts({ id }: { id: string }) {
    const response = await request(
      `/admin/sponsor/${id}/leaderboard/clear-all-abuse-coefficients`,
      {
        method: 'DELETE',
      },
    );

    return response;
  },

  async getSponsorsPrizes({ id }: { id: string }) {
    const response = await request<API.Service.SponsorPrize[]>(
      `/admin/sponsor/${id}/prize?size=100`,
      {
        method: 'GET',
      },
    );

    return response;
  },

  async addSponsorPrize({
    sponsorId,
    body,
  }: {
    sponsorId: string;
    body: API.Service.SponsorPrizeForm;
  }) {
    const response = await request(`/admin/sponsor/${sponsorId}/prize`, {
      method: 'POST',
      data: body,
    });

    return response;
  },

  async updateSponsorPrize({
    sponsorId,
    body,
  }: {
    sponsorId: string;
    body: API.Service.SponsorPrizeForm;
  }) {
    const response = await request(`/admin/sponsor/${sponsorId}/prize`, {
      method: 'PUT',
      data: body,
    });

    return response;
  },

  async deleteSponsorPrize({
    sponsorId,
    prizeId,
  }: {
    sponsorId: string;
    prizeId: string;
  }) {
    const response = await request(
      `/admin/sponsor/${sponsorId}/prize/${prizeId}`,
      {
        method: 'DELETE',
      },
    );

    return response;
  },

  async getSponsorDefinition({ id }: { id: string }) {
    const response = await request<API.Service.SponsorDefinition[]>(
      `/admin/sponsor/${id}/item-definition`,
      {
        method: 'GET',
      },
    );

    return response?.sort((a, b) => a.value - b.value);
  },

  async addSponsorDefinition({
    sponsorId,
    body,
  }: {
    sponsorId: string;
    body: API.Service.SponsorDefinition;
  }) {
    const response = await request(
      `/admin/sponsor/${sponsorId}/item-definition`,
      {
        method: 'POST',
        data: body,
      },
    );

    return response;
  },

  async updateSponsorDefinition({
    sponsorId,
    body,
  }: {
    sponsorId: string;
    body: API.Service.SponsorDefinition;
  }) {
    const response = await request(
      `/admin/sponsor/${sponsorId}/item-definition`,
      {
        method: 'PUT',
        data: body,
      },
    );

    return response;
  },

  async deleteSponsorDefinition({
    sponsorId,
    id,
  }: {
    sponsorId: string;
    id: number;
  }) {
    const response = await request(
      `/admin/sponsor/${sponsorId}/item-definition/${id}`,
      {
        method: 'DELETE',
      },
    );

    return response;
  },

  async getSponsorBlacklist(
    params: {
      sponsorId: string;

      pageSize?: number;
      current?: number;
    },
    sorter?: Record<string, SortOrder>,
    options?: { [key: string]: any },
  ) {
    const url = `/admin/sponsor/${params.sponsorId}/blacklist`;

    const sort = getSortParams(sorter);

    const current = params.current || 1;
    const pageSize = params.pageSize || 20;

    const response = await request<API.Service.SponsorBlacklistItem[]>(url, {
      method: 'GET',
      params: {
        page: current - 1,
        size: pageSize,
        ...(sort ? { sort } : {}),
      },
      getResponse: true,
      ...(options || {}),
    });

    return {
      data: response.data,
      total: parseInt(response.headers['x-total-count'], 10),
      success: true,
    };
  },

  async addSponsorBlacklist({
    sponsorId,
    body,
  }: {
    sponsorId: string;
    body: API.Service.SponsorBlacklistItemForm;
  }) {
    const response = await request(`/admin/sponsor/${sponsorId}/blacklist`, {
      method: 'POST',
      data: body,
    });

    return response;
  },

  async updateSponsorBlacklist({
    sponsorId,
    body,
  }: {
    sponsorId: string;
    body: API.Service.SponsorBlacklistItemForm;
  }) {
    const response = await request(`/admin/sponsor/${sponsorId}/blacklist`, {
      method: 'PUT',
      data: body,
    });

    return response;
  },

  async deleteSponsorBlacklist({
    sponsorId,
    id,
  }: {
    sponsorId: string;
    id: string;
  }) {
    const response = await request(
      `/admin/sponsor/${sponsorId}/blacklist/${id}`,
      {
        method: 'DELETE',
      },
    );

    return response;
  },

  async getSponsorMetrics(
    params: {
      id: string;
      name?: string;
      code?: string;
      pageSize?: number;
      current?: number;
    },
    sorter?: Record<string, SortOrder>,
    options?: { [key: string]: any },
  ): Promise<any> {
    const url = `/admin/sponsor/${params.id}/metrics`;

    // 1. 处理排序参数 (例如: "deposit,desc")
    const sort = getSortParams(sorter);

    // 2. 处理分页参数
    const current = params.current || 1;
    const pageSize = params.pageSize || 20;

    // 3. 发起请求
    const response = await request<API.Service.SponsorMetrics[]>(url, {
      method: 'GET',
      params: {
        page: current - 1,
        size: pageSize,
        name: params.name,
        code: params.code,  // 添加这一行
        ...(sort ? { sort } : {}),
      },
      getResponse: true,
      ...(options || {}),
    });

    // 4. 转换响应数据
    return {
      data: response.data?.map((item, index) => ({
        ...item,
        serialNumber: (current - 1) * pageSize + index + 1,
      })),
      total: parseInt(response.headers['x-total-count'] || '0', 10),
      success: true,
    };
  },
};

export default sponsorService;
